import { explainFirebaseError, isFirebaseConfigured, isPermissionDeniedError } from "data/firebase";
import {
  listAdApplicationsFromFirestore,
  listBusinessApplicationsFromFirestore,
  listDealApplicationsFromFirestore,
  listRegionApplicationsFromFirestore,
  listTourApplicationsFromFirestore,
  reviewAdApplicationInFirestore,
  reviewBusinessApplicationInFirestore,
  reviewDealApplicationInFirestore,
  reviewRegionApplicationInFirestore,
  reviewTourApplicationInFirestore,
} from "data/firebase/applicationsApi";
import { businessesRepo } from "data/repositories/businessesRepo";
import { dealsRepo } from "data/repositories/dealsRepo";

function wrap(error, fallback, collectionName) {
  if (isPermissionDeniedError(error)) {
    return new Error(
      `Firebase izin hatası: "${collectionName}" koleksiyonu için Firestore kuralları yayında değil. ` +
        "Depodaki firestore.rules dosyasını `firebase deploy --only firestore:rules` ile yayınlayın.",
    );
  }
  const explained = explainFirebaseError(error);
  if (explained && explained !== error?.message) return new Error(explained);
  return error instanceof Error ? error : new Error(fallback);
}

function requireConfigured() {
  if (!isFirebaseConfigured) throw new Error("Firebase yapılandırılmamış.");
}

function applicationToBusiness(application) {
  return {
    id: application.businessId,
    name: application.businessName,
    category: application.category,
    location: {
      provinceId: application.provinceId,
      district: application.district,
      addressLine: application.address,
    },
    contact: {
      phone: application.phone,
      email: application.email,
      website: application.website,
    },
    description: application.description,
    ...(application.imageUrl ? { imageUrl: application.imageUrl } : {}),
    ...(application.userId ? { ownerUserId: application.userId } : {}),
  };
}

function applicationToDeal(application) {
  return {
    id: application.id,
    provinceId: application.provinceId,
    title: application.title,
    description: application.description,
    priceText: application.priceText,
    startDate: application.startDate,
    endDate: application.endDate,
    theme: application.theme,
    contactCta: application.contactCta || "Detayları Gör",
  };
}

export const applicationsRepo = {
  async listBusinessApplications() {
    requireConfigured();
    try {
      return await listBusinessApplicationsFromFirestore();
    } catch (error) {
      throw wrap(error, "İşletme başvuruları yüklenemedi.", "businessApplications");
    }
  },

  async listDealApplications() {
    requireConfigured();
    try {
      return await listDealApplicationsFromFirestore();
    } catch (error) {
      throw wrap(error, "Fırsat başvuruları yüklenemedi.", "deals");
    }
  },

  async listAdApplications() {
    requireConfigured();
    try {
      return await listAdApplicationsFromFirestore();
    } catch (error) {
      throw wrap(error, "Reklam başvuruları yüklenemedi.", "ads");
    }
  },

  async listTourApplications() {
    requireConfigured();
    try {
      return await listTourApplicationsFromFirestore();
    } catch (error) {
      throw wrap(error, "Tur başvuruları yüklenemedi.", "travelTours");
    }
  },

  async listRegionApplications() {
    requireConfigured();
    try {
      return await listRegionApplicationsFromFirestore();
    } catch (error) {
      throw wrap(error, "Bölge başvuruları yüklenemedi.", "regionApplications");
    }
  },

  async reviewAd(id, status, note) {
    requireConfigured();
    try {
      return await reviewAdApplicationInFirestore(id, status, note);
    } catch (error) {
      throw wrap(error, "Başvuru güncellenemedi.", "ads");
    }
  },

  async reviewBusiness(id, status, note) {
    requireConfigured();
    let application;
    try {
      application = await reviewBusinessApplicationInFirestore(id, status, note);
    } catch (error) {
      throw wrap(error, "Başvuru güncellenemedi.", "businessApplications");
    }
    if (status === "approved") {
      const business = applicationToBusiness(application);
      const exists = businessesRepo.list({ includeDeleted: true }).some((item) => item.id === business.id);
      if (exists) businessesRepo.update(business.id, business);
      else businessesRepo.create(business);
    }
    return application;
  },

  async reviewDeal(id, status, note) {
    requireConfigured();
    let application;
    try {
      application = await reviewDealApplicationInFirestore(id, status, note);
    } catch (error) {
      throw wrap(error, "Başvuru güncellenemedi.", "deals");
    }
    const inCache = dealsRepo.list({ includeDeleted: true }).some((item) => item.id === application.id);
    if (status === "approved" && !inCache) {
      dealsRepo.create(applicationToDeal(application));
    }
    return application;
  },

  async reviewTour(id, status, note) {
    requireConfigured();
    try {
      return await reviewTourApplicationInFirestore(id, status, note);
    } catch (error) {
      throw wrap(error, "Tur başvurusu güncellenemedi.", "travelTours");
    }
  },

  async reviewRegion(id, status, note) {
    requireConfigured();
    try {
      return await reviewRegionApplicationInFirestore(id, status, note);
    } catch (error) {
      throw wrap(error, "Bölge başvurusu güncellenemedi.", "regionApplications");
    }
  },
};
